/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import { useCallback, useEffect, useState } from "react";
import type { Stripe } from "@stripe/stripe-js";
import {
  getStripeGeneration,
  getStripePromise,
  retryStripeIfFailed,
  subscribeStripeLoader,
} from "@/lib/stripe-loader";
import { useRecovery } from "@/hooks/use-recovery";

export interface StripePromiseState {
  /** The shared payment-library promise. */
  stripe: Promise<Stripe | null>;
  /** Changes only when a failed load is retried; use it as a React key. */
  generation: number;
  /** The current load was rejected (the script could not be fetched). */
  failed: boolean;
  /** Try the load again. Does nothing when the current load is fine. */
  retry: () => void;
}

/**
 * X-3 (S711 review): the payment library for a component, which re-requests it
 * when the connection comes back -- if, and only if, the load had failed.
 */
export function useStripePromise(): StripePromiseState {
  const read = () => ({ stripe: getStripePromise(), generation: getStripeGeneration() });
  const [state, setState] = useState(read);
  const [failed, setFailed] = useState(false);

  useEffect(() => subscribeStripeLoader(() => setState(read())), []);

  useEffect(() => {
    let live = true;
    setFailed(false);
    // Only a REJECTED load is a failure. `loadStripe` resolves to null solely
    // where there is no browser (server rendering, some test environments); in a
    // browser a script that cannot load rejects.
    state.stripe.then(
      () => {
        if (live) setFailed(false);
      },
      () => {
        if (live) setFailed(true);
      },
    );
    return () => {
      live = false;
    };
  }, [state.stripe]);

  const retry = useCallback(() => {
    retryStripeIfFailed();
  }, []);

  useRecovery(retry);

  return { ...state, failed, retry };
}
